// GapReport — Sprint 19BH Day 1 Step 1.3 · audit pass output.
//
// Why this exists (v4-rewrite.md §13.1):
//   The audit Phase 4 step.do produces one GapReport per ingest run. It is
//   serialized into IngestPackage.gapReport and stored on D1 results.
//   Frontend (Day 5 + 19BI) reads gapReport to render:
//     - a global badge with summary counts (errors / warns)
//     - per-chapter badges drilling to entries[]
//     - inline tooltips on items implicated by `itemIds`
//
// Summary as a precomputed object:
//   Recomputing counts on every UI render is cheap, but baking them into
//   the report makes it trivial to render a header without iterating
//   entries[] — and gives us a single comparable invariant for tests
//   (`summary.errorCount === entries.filter(...).length`).
import { z } from 'zod';
import { AuditInvariantSchema } from './audit-invariant.js';
import { GapEntrySchema } from './audit-gap-entry.js';
/** Aggregate counts. Recomputable from `entries` but precomputed for UI ergonomics. */
export const GapSummarySchema = z.object({
    errorCount: z.number().int().min(0)
        .describe('Count of entries with severity=error.'),
    warnCount: z.number().int().min(0)
        .describe('Count of entries with severity=warn.'),
    infoCount: z.number().int().min(0)
        .describe('Count of entries with severity=info.'),
}).passthrough();
export const GapReportSchema = z.object({
    /** ISO timestamp when this report was produced. */
    generatedAt: z.string().datetime()
        .describe('ISO timestamp when the audit step.do completed.'),
    /** The invariants that were dispatched during this run. */
    invariantsRun: z.array(AuditInvariantSchema)
        .describe('Snapshot of invariants the audit framework executed. Lets the UI know which checks were skipped.'),
    /** One entry per gap found. */
    entries: z.array(GapEntrySchema)
        .describe('All gaps found. May be empty (clean ingest).'),
    /** Precomputed aggregate counts. */
    summary: GapSummarySchema
        .describe('Precomputed severity counts. Always = entries.filter(...).length per severity.'),
}).passthrough();
/**
 * Construct a default empty report. Used by:
 *   - finalize.ts (Day 3 Step 3.6) when wiring Phase 5 with `gapReport ??
 *     defaultEmptyGapReport()`.
 *   - migrateResultV3ToV4 (Day 5 Step 5.1) when migrating old IDB records
 *     that have no gapReport at all.
 */
export function defaultEmptyGapReport(generatedAt = new Date().toISOString()) {
    return {
        generatedAt,
        invariantsRun: [],
        entries: [],
        summary: { errorCount: 0, warnCount: 0, infoCount: 0 },
    };
}
/** Recompute summary from entries. Used by audit/index.ts (Day 3) when assembling reports. */
export function computeGapSummary(entries) {
    let errorCount = 0;
    let warnCount = 0;
    let infoCount = 0;
    for (const entry of entries) {
        if (entry.severity === 'error')
            errorCount++;
        else if (entry.severity === 'warn')
            warnCount++;
        else
            infoCount++;
    }
    return { errorCount, warnCount, infoCount };
}
//# sourceMappingURL=audit-gap-report.js.map